/**
 * Security event logging utility
 */
import { supabase } from '@/integrations/supabase/client';
import { productionLogger } from './production-logger';

export interface SecurityEvent {
  event_type: 'failed_login' | 'rate_limit_exceeded' | 'suspicious_activity' | 'admin_action' | 'data_access';
  user_id?: string;
  ip_address?: string;
  user_agent?: string;
  details?: Record<string, unknown>;
  severity: 'low' | 'medium' | 'high' | 'critical';
}

class SecurityLogger {
  private events: SecurityEvent[] = [];
  private batchSize = 10;
  private flushInterval = 30000; // 30 seconds
  private intervalId: NodeJS.Timeout | null = null;

  constructor() {
    // CRITICAL: Only initialize if browser APIs are available (prevents SSR crash)
    if (typeof window !== 'undefined') {
      // Flush events periodically
      this.intervalId = setInterval(() => {
        this.flush();
      }, this.flushInterval);

      // Flush on page unload
      window.addEventListener('beforeunload', () => {
        this.flush();
      });
    }
  }

  /**
   * Log a security event
   */
  logEvent(event: SecurityEvent) {
    const enhancedEvent = {
      ...event,
      timestamp: new Date().toISOString(),
      ip_address: event.ip_address || this.getClientIP(),
      user_agent: event.user_agent || (typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown'),
    };

    this.events.push(enhancedEvent);

    // Flush immediately for critical events
    if (event.severity === 'critical') {
      this.flush();
    } else if (this.events.length >= this.batchSize) {
      this.flush();
    }
  }

  /**
   * Flush events to the server
   */
  private async flush() {
    if (this.events.length === 0) return;

    const eventsToFlush = [...this.events];
    this.events = [];

    try {
      // Check if user is authenticated before attempting to flush
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        // User is not authenticated, discard events silently
        productionLogger.info('Skipping security event flush - user not authenticated');
        return;
      }

      // Use the security monitor edge function for better security
      await supabase.functions.invoke('security-monitor', {
        body: {
          action: 'log_event',
          events: eventsToFlush
        }
      });
      
      productionLogger.info('Security events flushed successfully');
    } catch (error) {
      productionLogger.error('Failed to flush security events:', error);
      // Re-queue events on failure (but limit to prevent memory issues)
      if (this.events.length < 100) {
        this.events.unshift(...eventsToFlush.slice(0, 50));
      }
    }
  }

  /**
   * Get client IP (limited in browser environment)
   */
  private getClientIP(): string | undefined {
    // In browser, we can't get real IP. Let the server infer IP from the request; omit field.
    return undefined;
  }

  /**
   * Log failed authentication attempt
   */
  logFailedAuth(email?: string, reason?: string) {
    this.logEvent({
      event_type: 'failed_login',
      details: { email, reason },
      severity: 'medium'
    });
  }

  /**
   * Log rate limit exceeded
   */
  logRateLimitExceeded(endpoint: string, identifier: string) {
    this.logEvent({
      event_type: 'rate_limit_exceeded',
      details: { endpoint, identifier },
      severity: 'medium'
    });
  }

  /**
   * Log suspicious activity
   */
  logSuspiciousActivity(description: string, details?: Record<string, unknown>) {
    this.logEvent({
      event_type: 'suspicious_activity',
      details: { description, ...details },
      severity: 'high'
    });
  }

  /**
   * Log admin actions
   */
  logAdminAction(action: string, target?: string, userId?: string) {
    this.logEvent({
      event_type: 'admin_action',
      user_id: userId,
      details: { action, target },
      severity: 'low'
    });
  }

  /**
   * Log sensitive data access
   */
  logDataAccess(table: string, operation: string, userId?: string) {
    this.logEvent({
      event_type: 'data_access',
      user_id: userId,
      details: { table, operation },
      severity: 'low'
    });
  }

  /**
   * Clear all pending events (useful on logout)
   */
  clearEvents() {
    this.events = [];
    productionLogger.info('Security event queue cleared');
  }
}

export const securityLogger = new SecurityLogger();